import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/Layout";
import { ChevronLeft, ChevronRight, MapPin, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCalendarEvents, classifyDate } from "@/lib/calendarEvents";
import { toast } from "sonner";

const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
function mondayOf(dateStr) {
  const d = new Date(dateStr);
  const day = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - day);
  return d;
}

export default function Calendar() {
  const [date, setDate] = useState(iso(new Date()));
  const [view, setView] = useState("day");
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [delEntry, setDelEntry] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const events = useCalendarEvents();

  const loadJobs = () => api.get("/jobs").then((r) => setJobs(r.data));
  useEffect(() => { loadJobs(); }, []);
  useEffect(() => { api.get("/assignable-workers").then((r) => setWorkers(r.data)).catch(() => {}); }, []);

  const removeEntry = async () => {
    if (!delEntry) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${delEntry.jobId}/remove-day`, { kind: "calendar_day", date: delEntry.date });
      toast.success("Removed from the calendar.");
      setDelEntry(null);
      await loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove this booking.");
    } finally { setBusy(false); }
  };

  const shift = (n) => { const d = new Date(date); d.setDate(d.getDate() + (view === "week" ? n * 7 : n)); setDate(iso(d)); };

  // Flatten jobs into one calendar entry per visit: each completed daily docket on its own visit_date,
  // plus the active/scheduled slot on the current appointment_date (so return-visit history stays visible).
  const entries = useMemo(() => {
    const out = [];
    // Name/email -> worker_id map so a completed day can be credited to whoever ACTUALLY did it
    // (matches the backend Timesheet attribution, which resolves completed_by by name).
    const nameToWid = {};
    (workers || []).forEach((w) => { if (w.name) nameToWid[w.name.trim().toLowerCase()] = w.id; });
    (jobs || []).forEach((j) => {
      const jobWorkerIds = (j.technician_ids && j.technician_ids.length) ? j.technician_ids : (j.technician_id ? [j.technician_id] : []);
      // De-dupe per (date + worker) so multiple workers rostered on the SAME date each get their
      // own calendar entry, while a completed docket still supersedes that worker's scheduled slot.
      const covered = new Set();
      const mark = (dt, wids) => (wids.length ? wids : ["__none__"]).forEach((w) => covered.add(`${dt}|${w}`));
      (j.dockets || []).forEach((d, i) => {
        if (d.archived) return;
        const vd = (d.visit_date || "").slice(0, 10);
        if (!vd) return;
        // Attribute the completed day to whoever ACTUALLY did it — mirrors Timesheets:
        // completed_by_id -> name match (completed_by / technician_name / entered_by)
        // -> that day's roster -> the sole job worker.
        const sdM = (j.schedule_days || []).find((sd) => (sd.date || "").slice(0, 10) === vd);
        const tos = d.time_on_site || {};
        let doerWid = d.completed_by_id || null;
        if (!doerWid) {
          for (const cand of [d.completed_by, d.technician_name, tos.entered_by]) {
            const nm = (cand || "").trim().toLowerCase();
            if (nm && nameToWid[nm]) { doerWid = nameToWid[nm]; break; }
          }
        }
        if (!doerWid && sdM && sdM.worker_ids && sdM.worker_ids.length) doerWid = sdM.worker_ids[0];
        if (!doerWid && jobWorkerIds.length === 1) doerWid = jobWorkerIds[0];
        const dayWids = doerWid ? [doerWid] : ((sdM && sdM.worker_ids && sdM.worker_ids.length) ? sdM.worker_ids : jobWorkerIds);
        mark(vd, dayWids);
        const doerName = (workers.find((w) => w.id === doerWid) || {}).name;
        out.push({
          key: `${j.id}-d${i + 1}`, jobId: j.id, date: vd,
          start_time: d.start_time || j.start_time, customer_name: j.customer_name,
          site_address: j.site_address,
          technician_name: doerName || (sdM && sdM.worker_name) || d.completed_by || j.technician_name,
          worker_ids: dayWids,
          status: "completed", docketLabel: d.docket_number,
        });
      });
      const ad = (j.appointment_date || "").slice(0, 10);
      if (Array.isArray(j.schedule_days) && j.schedule_days.length) {
        j.schedule_days.forEach((sd, i) => {
          const vd = (sd.date || "").slice(0, 10);
          if (!vd) return;
          const wids = (sd.worker_ids && sd.worker_ids.length) ? sd.worker_ids : jobWorkerIds;
          const uncovered = (wids.length ? wids : ["__none__"]).filter((w) => !covered.has(`${vd}|${w}`));
          if (!uncovered.length) return;
          uncovered.forEach((w) => covered.add(`${vd}|${w}`));
          // Each planned day carries its OWN status. A completed day renders from its docket above.
          // A day sent for the customer's signature (tracked per-day in pending_dockets) shows
          // "awaiting signature"; the job's active day shows "in progress"; everything else is "scheduled".
          let dayStatus = "scheduled";
          const pendingSet = new Set((j.pending_dockets || []).map((p) => (p.visit_date || "").slice(0, 10)));
          const legacyAwaiting = (!(j.pending_dockets || []).length && j.status === "awaiting_signature") ? (j.awaiting_signature_date || ad || "").slice(0, 10) : "";
          if (pendingSet.has(vd) || (legacyAwaiting && vd === legacyAwaiting)) dayStatus = "awaiting_signature";
          else if (j.status === "in_progress" && vd === ad) dayStatus = "in_progress";
          out.push({
            key: `${j.id}-s${i + 1}`, jobId: j.id, date: vd, start_time: sd.start_time || j.start_time,
            customer_name: j.customer_name, site_address: j.site_address,
            technician_name: sd.worker_name || j.technician_name,
            worker_ids: uncovered.filter((w) => w !== "__none__"),
            status: dayStatus,
          });
        });
      } else if (ad) {
        const wids = jobWorkerIds.length ? jobWorkerIds : ["__none__"];
        const uncovered = wids.filter((w) => !covered.has(`${ad}|${w}`));
        if (uncovered.length) {
          out.push({
            key: j.id, jobId: j.id, date: ad, start_time: j.start_time,
            customer_name: j.customer_name, site_address: j.site_address,
            technician_name: j.technician_name, worker_ids: uncovered.filter((w) => w !== "__none__"), status: j.status,
          });
        }
      }
    });
    return out;
  }, [jobs, workers]);

  const dayJobs = (ds) => entries.filter((e) => e.date === ds).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));

  // Group a day's jobs by assigned worker — employees (technicians) first, then contractors, then any
  // non-rostered/inactive workers, then unassigned. Each worker row shows their jobs for that day.
  const dayByWorker = (ds) => {
    const list = dayJobs(ds);
    const groupRank = (role) => (role === "contractor" ? 1 : role === "other" ? 2 : 0);
    const rosterRows = [...workers]
      .sort((a, b) => groupRank(a.role) - groupRank(b.role) || (a.name || "").localeCompare(b.name || ""))
      .map((w) => ({ id: w.id, name: w.name, role: w.role, jobs: [],
        offDuty: w.worker_type === "rotating" && ((w.start_date && ds < w.start_date) || (w.end_date && ds > w.end_date)) }));
    const byId = Object.fromEntries(rosterRows.map((r) => [r.id, r]));
    const extra = {};
    const unassigned = { id: "__unassigned__", name: "Unassigned", role: "none", jobs: [] };
    list.forEach((e) => {
      const wids = (e.worker_ids || []).filter(Boolean);
      if (!wids.length) { unassigned.jobs.push(e); return; }
      wids.forEach((wid) => {
        if (byId[wid]) byId[wid].jobs.push(e);
        else {
          if (!extra[wid]) extra[wid] = { id: wid, name: e.technician_name || "Unknown", role: "other", jobs: [] };
          extra[wid].jobs.push(e);
        }
      });
    });
    const extraRows = Object.values(extra).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const rows = [...rosterRows.filter((r) => !r.offDuty || r.jobs.length), ...extraRows];
    if (unassigned.jobs.length) rows.push(unassigned);
    return rows;
  };

  const weekStart = mondayOf(date);
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const label = view === "day"
    ? new Date(date).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : `${weekStart.toLocaleDateString("en-AU", { day: "numeric", month: "short" })} – ${weekDays[6].toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Calendar</h1><p className="text-slate-500 mt-1">{label}</p></div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-slate-100 rounded-lg p-1">
            {["day", "week"].map((v) => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${view === v ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}>{v}</button>
            ))}
          </div>
          <Button data-testid="cal-prev" variant="outline" size="icon" onClick={() => shift(-1)}><ChevronLeft className="w-4 h-4" /></Button>
          <Button data-testid="cal-today" variant="outline" onClick={() => setDate(iso(new Date()))}>Today</Button>
          <Button data-testid="cal-next" variant="outline" size="icon" onClick={() => shift(1)}><ChevronRight className="w-4 h-4" /></Button>
          {view === "day" && <input data-testid="cal-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />}
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-slate-500" data-testid="calendar-legend">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />Public Holiday</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-orange-500" />Construction RDO</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" />Holiday &amp; RDO</span>
      </div>

      {view === "day" ? (
        <div className="space-y-3">
          {(() => { const di = classifyDate(events, date); return (di.holiday || di.rdo) ? (
            <div data-testid="day-event-banner" className={`rounded-xl border p-3 text-sm ${di.both ? "bg-red-50 border-red-200 text-red-800" : di.holiday ? "bg-yellow-50 border-yellow-200 text-yellow-800" : "bg-orange-50 border-orange-200 text-orange-800"}`}>
              {di.holiday && <span className="font-semibold">🟡 Public Holiday: {di.holiday}. </span>}
              {di.rdo && <span className="font-semibold">🟠 Construction Industry RDO: {di.rdo}.</span>}
            </div>
          ) : null; })()}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="day-worker-board">
          {dayByWorker(date).length === 0 && <p className="text-slate-400 text-center py-16">No employees or contractors to display.</p>}
          {dayByWorker(date).map((w) => (
            <div key={w.id} data-testid={`cal-worker-row-${w.id}`} className="flex flex-col sm:flex-row sm:items-stretch">
              <div className="sm:w-48 shrink-0 px-5 py-4 bg-slate-50/60 sm:border-r border-slate-100 flex sm:flex-col justify-between sm:justify-start gap-1">
                <p className="font-heading font-semibold text-navy truncate">{w.name}</p>
                <span className={`text-[10px] uppercase tracking-wide font-bold self-start px-2 py-0.5 rounded-full ${w.role === "contractor" ? "bg-purple-100 text-purple-700" : w.role === "technician" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                  {w.role === "technician" ? "Employee" : w.role === "contractor" ? "Contractor" : w.role === "none" ? "—" : "Inactive"}
                </span>
              </div>
              <div className="flex-1 min-w-0 p-3 sm:p-4 space-y-2">
                {w.jobs.length === 0 ? (
                  <p className="text-sm text-slate-300 py-2">No jobs scheduled</p>
                ) : w.jobs.map((j) => (
                  <div key={`${w.id}-${j.key}`} data-testid={`cal-job-${j.jobId}`} onClick={() => navigate(`/jobs/${j.jobId}`)}
                    className="flex items-center gap-4 rounded-xl border border-slate-100 px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors">
                    <div className="w-14 shrink-0 text-navy font-semibold text-sm">{j.start_time || "—"}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-navy truncate">{j.customer_name}</p>
                      <p className="text-sm text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{j.site_address}</p>
                    </div>
                    <StatusPill status={j.status} />
                    <button data-testid={`cal-del-${j.jobId}-${j.date}`} title="Remove from calendar"
                      onClick={(ev) => { ev.stopPropagation(); setDelEntry(j); }}
                      className="shrink-0 text-slate-300 hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {weekDays.map((d) => {
            const ds = iso(d);
            const list = dayJobs(ds);
            const isToday = ds === iso(new Date());
            const di = classifyDate(events, ds);
            return (
              <div key={ds} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 min-h-[140px]">
                <div className={`text-center pb-2 mb-2 border-b border-slate-100 ${isToday ? "text-navy" : "text-slate-500"}`}>
                  <p className="text-xs uppercase tracking-wider">{d.toLocaleDateString("en-AU", { weekday: "short" })}</p>
                  <p className={`font-heading font-bold text-lg ${isToday ? "text-white bg-navy rounded-full w-8 h-8 flex items-center justify-center mx-auto" : ""}`}>{d.getDate()}</p>
                  {di.dot && <span title={di.holiday || di.rdo || ""} data-testid={`cal-event-dot-${ds}`} className={`inline-block w-2 h-2 rounded-full mt-1 ${di.dot}`} />}
                </div>
                <div className="space-y-1.5">
                  {list.length === 0 && <p className="text-[11px] text-slate-300 text-center">—</p>}
                  {list.map((j) => (
                    <button key={j.key} data-testid={`cal-week-job-${j.jobId}`} onClick={() => navigate(`/jobs/${j.jobId}`)}
                      className={`w-full text-left rounded-lg px-2 py-1.5 text-xs ${j.status === "in_progress" ? "bg-emerald-50" : j.status === "completed" ? "bg-slate-100" : j.status === "return_tomorrow" ? "bg-orange-50" : j.status === "awaiting_return_visit" ? "bg-purple-50" : j.status === "awaiting_signature" ? "bg-yellow-50" : "bg-blue-50"} hover:opacity-80`}>
                      <p className="font-semibold text-navy">{j.start_time || "—"}</p>
                      <p className="text-slate-600 truncate">{j.customer_name}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {delEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="cal-del-dialog">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-heading font-bold text-navy text-lg">Remove from calendar?</h3>
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-navy">{delEntry.customer_name}</span> — {new Date(delEntry.date).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}.
              {delEntry.status === "completed"
                ? " This day has recorded work, so it will be archived (records kept) and taken off the calendar."
                : " This booking day will be removed from the calendar."}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDelEntry(null)} data-testid="cal-del-cancel" disabled={busy}>Cancel</Button>
              <Button onClick={removeEntry} data-testid="cal-del-confirm" disabled={busy} className="bg-red-600 hover:bg-red-700 text-white">{busy ? "Removing…" : "Remove"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
